import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Upload, Target, Trophy, Users, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function Dashboard({ user }) {
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState({
    totalUploads: 0,
    totalPlayers: 0,
    divisions: 0
  });
  const [dragActive, setDragActive] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [scResponse, stResponse] = await Promise.all([
        axios.get(`${API}/scorecards`),
        axios.get(`${API}/standings`)
      ]);
      
      // Get total players from Overall division only
      const overallDiv = stResponse.data.find(div => div.division === 'Overall');
      const totalPlayers = overallDiv?.total_players || overallDiv?.standings?.length || 0;
      
      // Count only non-Overall divisions
      const divisionCount = stResponse.data.filter(div => div.division !== 'Overall').length;
      
      setStats({
        totalUploads: scResponse.data.length,
        totalPlayers: totalPlayers,
        divisions: divisionCount
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    if (!(file.name.endsWith('.pdf') || file.name.endsWith('.html'))) {
      toast.error('Only PDF and HTML files are allowed');
      return;
    }

    setUploading(true);
    setDuplicateWarning(null);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/scorecards/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Check if upload has duplicates requiring admin review
      if (response.data.has_duplicates) {
        setDuplicateWarning({
          message: response.data.message,
          reviewId: response.data.pending_review?.id,
          duplicates: response.data.pending_review?.duplicates || []
        });
        toast.warning('Scorecard flagged for admin review due to duplicate players', { duration: 5000 });
      } else {
        toast.success('Scorecard uploaded successfully!');
        fetchStats();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Upload failed';
      if (error.response?.status === 409) {
        // Duplicate date detected
        toast.error(errorMsg, { duration: 5000 });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  return (
    <Layout user={user}>
      <div className="max-w-7xl mx-auto p-4 md:p-8 lg:p-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-barlow font-bold text-4xl md:text-5xl text-slate-900 uppercase tracking-tight mb-2">
            Dashboard
          </h1>
          <p className="font-inter text-stone-600">Upload scorecards and track competition standings</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-stone-200 rounded-sm p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-600 font-inter text-sm uppercase tracking-wider mb-1">Total Uploads</p>
                <p className="font-barlow font-bold text-3xl text-slate-900">{stats.totalUploads}</p>
              </div>
              <Upload className="w-10 h-10 text-amber-600" strokeWidth={1.5} />
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-sm p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-600 font-inter text-sm uppercase tracking-wider mb-1">Total Players</p>
                <p className="font-barlow font-bold text-3xl text-slate-900">{stats.totalPlayers}</p>
              </div>
              <Users className="w-10 h-10 text-amber-600" strokeWidth={1.5} />
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-sm p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-600 font-inter text-sm uppercase tracking-wider mb-1">Divisions</p>
                <p className="font-barlow font-bold text-3xl text-slate-900">{stats.divisions}</p>
              </div>
              <Trophy className="w-10 h-10 text-amber-600" strokeWidth={1.5} />
            </div>
          </div>
        </div>

        {/* Duplicate Warning */}
        {duplicateWarning && (
          <div className="bg-amber-50 border border-amber-300 rounded-sm p-6 mb-8">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" strokeWidth={1.5} />
              <div className="flex-1">
                <h3 className="font-barlow font-bold text-lg text-amber-800 uppercase mb-2">
                  Duplicate Players Detected
                </h3>
                <p className="font-inter text-amber-700 mb-3">
                  {duplicateWarning.message}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {duplicateWarning.duplicates.map((dup, idx) => (
                    <span key={idx} className="bg-amber-200 text-amber-800 text-sm px-3 py-1 rounded font-inter">
                      {dup.name} ({dup.division}): {dup.scores.length} entries
                    </span>
                  ))}
                </div>
                {user.is_admin ? (
                  <Button
                    onClick={() => navigate('/admin/duplicates')}
                    className="bg-amber-600 text-white hover:bg-amber-700 rounded-sm uppercase tracking-wider font-bold font-barlow"
                  >
                    Review Duplicates
                  </Button>
                ) : (
                  <p className="font-inter text-sm text-amber-700">
                    Please contact an administrator to resolve this upload.
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDuplicateWarning(null)}
                className="text-amber-600 hover:bg-amber-100"
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {/* Upload Zone */}
        <div className="bg-white border border-stone-200 rounded-sm p-8 shadow-sm">
          <h2 className="font-barlow font-bold text-2xl text-slate-900 uppercase tracking-tight mb-4">
            Upload Scorecard
          </h2>
          
          <div
            data-testid="upload-zone"
            className={`upload-zone rounded-sm p-12 text-center ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Target className="w-16 h-16 text-amber-600 mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-barlow font-semibold text-lg text-slate-900 mb-2">Drop PDF/HTML here or click to upload</p>
            <p className="font-inter text-sm text-stone-600 mb-6">Accepts .pdf and .html files</p>
            
            <label htmlFor="file-upload">
              <Button
                data-testid="upload-button"
                disabled={uploading}
                className="bg-amber-600 text-white hover:bg-amber-700 rounded-sm uppercase tracking-wider font-bold px-6 py-3 font-barlow h-auto"
                onClick={() => document.getElementById('file-upload').click()}
              >
                {uploading ? 'Uploading...' : 'Select File'}
              </Button>
            </label>
            <input
              id="file-upload"
              data-testid="file-input"
              type="file"
              accept=".pdf,.html"
              onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0])}
              className="hidden"
            />
          </div>
        </div>
      </div>
    </Layout>
  );
}